import {
  InteriorQuality as InteriorQualityType,
  RealEstateCategory,
  Salutation,
  SegmentationType,
  TransactionType,
} from '../../../../../../@types/types'
import { Flow, FlowName, FlowScreenElement } from '../../../../../../@types/flow-types'
import { PROPERTY_OWNER_APARTMENT_RENT_FLOW_PATH } from '../../../../../../services/routes/routes'
import { priceToApiMonthlyRent } from '../../../../../../api/realEstate/converters/toApi/estate/price/priceToApi'
import { webViewPropertyOwnerApartmentRentSchema } from './webViewPropertyOwnerApartmentRentSchema'
import {
  SCREEN_ADDRESS,
  SCREEN_ADDTIONAL_INFORMATION_CTA,
  SCREEN_BASIC_INFO,
  SCREEN_CONTACT_INFO,
  SCREEN_COSTS,
  SCREEN_DESCRIPTION,
  SCREEN_MULTIMEDIA,
  SCREEN_OBJECT_DETAILS,
  SCREEN_WEB_VIEW_AMENITIES,
  SCREENS,
} from '../../../../../../services/domain/wizardScreens'
import Address from '../../../../../../components/pages/insertionPage/screens/address/Address'
import BasicInfo from '../../../../../../components/pages/insertionPage/screens/basicInfo/BasicInfo'
import WebViewCostsRent from '../../../../../../components/pages/insertionPage/screens/costs/wrappers/webView/WebViewCostsRent'
import {
  livingSpaceField,
  roomField,
  startRentalDateWithToggleField,
} from '../../../../../../components/pages/insertionPage/screens/basicInfo/wrappers/basicInfoFieldsWrapper'
import BuildingType from '../../../../../../components/pages/insertionPage/screens/commonFields/buildingType/BuildingType'
import { createApartmentRentEstateFeaturesForWebView, getApartmentRentBuildingTypeOptions } from '../common'
import {
  floorNumberField,
  heatingTypeField,
  objectConditionField,
  totalFloorsField,
} from '../../../../../../components/pages/insertionPage/screens/objectDetails/wrappers/objectDetailsFieldsWrapper'
import React from 'react'
import Description from '../../../../../../components/pages/insertionPage/screens/description/Description'
import { ObjectDetails } from '../../../../../../components/pages/insertionPage/screens/objectDetails/ObjectDetails'
import { AdditionalInformationCta } from '../../../../../../components/pages/insertionPage/screens/objectDetailsCta/AdditionalInformationCta'
import ContactInfo from '../../../../../../components/pages/insertionPage/screens/contactInfo/ContactInfo'
import { WebViewMultimedia } from '../../../../../../components/pages/insertionPage/screens/multimediaNew/webView/WebViewMultimedia'
import { MultimediaUploaderType } from '../../../../../../@types/multimedia-types'

import WebViewAmenities from '../../../../../../components/pages/insertionPage/screens/otherFacilities/webView/WebViewAmenities'
import {
  interiorQualityField,
  petsAllowed,
  webViewParkingSpace,
} from '../../../../../../components/pages/insertionPage/screens/otherFacilities/wrapper/otherFacilitiesFieldsWrapper'
import { AmenitiesLabel } from '../../../../../../components/pages/insertionPage/screens/otherFacilities/webView/AmenitiesLabel'

const webViewApartmentRentFlowScreens: FlowScreenElement[] = [
  {
    screen: SCREENS[SCREEN_MULTIMEDIA],
    element: (props) => (
      <WebViewMultimedia
        {...props}
        doCloseMultimediaStatusMessage={props.doCloseMultimediaStatusMessage!}
        allowedTypesToDisplay={[MultimediaUploaderType.PICTURE]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_ADDRESS],
    element: (props) => <Address {...props} isPriceOverviewLinkDisplayed={true} />,
  },
  {
    screen: SCREENS[SCREEN_BASIC_INFO],
    element: (props) => (
      <BasicInfo
        {...props}
        showMandatoryHint={false}
        properties={[
          livingSpaceField('full', undefined, true),
          roomField('one-whole margin-top-l', 'wizard.screen.basicInfo.room.label', true),
          startRentalDateWithToggleField('margin-top-l grid-item one-whole'),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_COSTS],
    element: (props) => <WebViewCostsRent {...props} />,
  },
  {
    screen: SCREENS[SCREEN_DESCRIPTION],
    element: (props) => (
      <Description
        {...props}
        hasFurnishingNote={true}
        hasLocationNote={true}
        hasOtherNote={true}
        hasHomeStreetHome={true}
      />
    ),
  },

  {
    screen: SCREENS[SCREEN_CONTACT_INFO],
    element: (props) => <ContactInfo {...props} />,
  },
  {
    screen: SCREENS[SCREEN_ADDTIONAL_INFORMATION_CTA],
    element: () => <AdditionalInformationCta />,
  },
  {
    screen: SCREENS[SCREEN_OBJECT_DETAILS],
    element: (props) => (
      <ObjectDetails
        {...props}
        showMandatoryHint={false}
        properties={[
          (props) => (
            <BuildingType
              {...props}
              buildingTypeOptions={getApartmentRentBuildingTypeOptions()}
              wrapperClassName='grid-item one-whole'
              categoryLabel='apartment'
            />
          ),
          floorNumberField('one-whole margin-top-l'),
          totalFloorsField('one-whole margin-top-l'),
          objectConditionField('one-whole margin-top-l'),
          heatingTypeField('one-whole margin-top-l'),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_WEB_VIEW_AMENITIES],
    element: (props) => (
      <WebViewAmenities
        {...props}
        properties={[
          createApartmentRentEstateFeaturesForWebView(<AmenitiesLabel />, 'grid-item one-whole'),
          interiorQualityField('grid-item one-whole margin-top-l'),
          webViewParkingSpace('grid-item one-whole margin-top-xl'),
          petsAllowed('grid-item one-whole margin-top-l', false, true),
        ]}
      />
    ),
  },
]

export const webViewPropertyOwnerApartmentRentFlow: Flow = {
  flowInformation: {
    isMobileFlow: true,
    owner: 'wizard.navigation.header.owner',
    estateType: 'wizard.navigation.header.apartment',
    offerType: 'wizard.navigation.header.rents',
    path: PROPERTY_OWNER_APARTMENT_RENT_FLOW_PATH,
    name: FlowName.PROPERTY_OWNER_APARTMENT_RENT,
    validationSchema: webViewPropertyOwnerApartmentRentSchema,
    options: {
      hasTextSuggestions: true,
      depositCalculationEnabled: true,
    },
    metaData: {
      category: RealEstateCategory.APARTMENT,
      segmentation: SegmentationType.PROPERTY_OWNER,
      transaction: TransactionType.RENT,
    },
    priceToApiMapper: priceToApiMonthlyRent,
  },
  defaultStateValues: {
    heatingCostsOption: true,
    unknownConstructionYear: false,
    hideStreetAndStreetNumber: false,
    estateFeatures: {},
    interiorQuality: InteriorQualityType.NO_INFORMATION,
    salutation: Salutation.NO_SALUTATION,
  },
  flowScreens: webViewApartmentRentFlowScreens,
}
